import type { SaleFeedStatusInfo } from '@auctisync/api-client';
import { formatInZone, resolveTimeZone } from '@auctisync/shared';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useConnection } from '../../hooks/useConnection';

/**
 * What a live page says when the auction engine's feed is not live.
 *
 *   paused        — down past the feed's threshold (`pausedAfterMs`, 30 s by
 *                   default: the engine's provisional outage line, design §7)
 *                   while the sale is running. Jim's words, 2026-10-08:
 *                   "Bidding paused — your bids are safe." The engine holds
 *                   every bid it has acknowledged and extends the lots by the
 *                   time it was down, so nobody loses a lot to the outage.
 *   reconnecting  — a drop shorter than that. Said only once it has lasted
 *                   `RECONNECTING_SHOWS_AFTER_MS`: most drops are a blink, and
 *                   a bar that flashes for half a second is noise. Not said
 *                   at all while the site-wide strip (`ConnectionBar`) is up —
 *                   it already says the same thing in the same words.
 *   closed        — the feed stopped for good (the engine refused it); the
 *                   page asks for a refresh rather than pretending.
 *
 * 🔴 NEUTRAL, NEVER RED — the same locked rule as `ConnectionBar`: an outage
 * borrowing an auction colour teaches a bidder to read it as "you are losing".
 *
 * The "prices as of" time is the AUCTION's local time, zone named (memory:
 * feedback_times_are_the_auctions_local_time) — never the viewer's.
 */
export const RECONNECTING_SHOWS_AFTER_MS = 3_000;

/**
 * The one status to show for a page with several sales' feeds (My bids, the
 * Watchlist): a paused sale first — the bidder's bids are what they need to
 * hear about — then a stopped feed, then a reconnect. Null when all are live.
 */
export function mostUrgentStatus<T extends { info: SaleFeedStatusInfo }>(
  statuses: readonly T[],
): T | null {
  const rank = (s: SaleFeedStatusInfo): number => {
    if (s.status === 'paused') return 3;
    if (s.status === 'closed' && s.closedReason !== 'closed_by_caller') return 2;
    if (s.status === 'reconnecting') return 1;
    return 0;
  };
  let best: T | null = null;
  for (const s of statuses) {
    if (rank(s.info) > (best === null ? 0 : rank(best.info))) best = s;
  }
  return best;
}

export interface LiveStatusBannerProps {
  status: SaleFeedStatusInfo | null;
  /** The sale's time zone (`auctions.timezone`); falls back like every sale time. */
  timeZone: string | null | undefined;
  /** The house's own pages say it without "your". */
  audience?: 'bidder' | 'staff';
  /** Device clock, injected for tests. */
  now?: () => number;
}

export function LiveStatusBanner({
  status,
  timeZone,
  audience = 'bidder',
  now = Date.now,
}: LiveStatusBannerProps) {
  const { state: site } = useConnection();
  const [, setTick] = useState(0);
  const reconnecting = status?.status === 'reconnecting';

  // A reconnecting bar appears only once the drop has lasted; tick until it shows.
  useEffect(() => {
    if (!reconnecting) return;
    const id = setInterval(() => setTick((n) => n + 1), 1_000);
    return () => clearInterval(id);
  }, [reconnecting]);

  if (status === null) return null;
  const staff = audience === 'staff';

  if (status.status === 'paused') {
    const asOf =
      status.downSince === null
        ? null
        : formatInZone(new Date(status.downSince).toISOString(), resolveTimeZone(timeZone), {
            hour: 'numeric',
            minute: '2-digit',
          });
    return (
      <Bar staff={staff} strong testId="live-status-paused">
        <b>
          {staff ? 'Live view paused — bids are safe.' : 'Bidding paused — your bids are safe.'}
        </b>
        <span>{asOf === null ? 'Reconnecting…' : `Prices as of ${asOf}. Reconnecting…`}</span>
      </Bar>
    );
  }

  if (status.status === 'reconnecting') {
    if (site !== 'online') return null;
    if (status.downSince !== null && now() - status.downSince < RECONNECTING_SHOWS_AFTER_MS) {
      return null;
    }
    return (
      <Bar staff={staff} strong={false} testId="live-status-reconnecting">
        <b>Reconnecting</b>
        <span>Prices may be a few seconds behind.</span>
      </Bar>
    );
  }

  if (status.status === 'closed' && status.closedReason !== 'closed_by_caller') {
    return (
      <Bar staff={staff} strong testId="live-status-stopped">
        <b>Live updates stopped</b>
        <span>Refresh the page to start them again.</span>
        <button
          type="button"
          className={staff ? undefined : 'wcb-b'}
          style={staff ? staffButton : undefined}
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </Bar>
    );
  }

  return null;
}

function Bar({
  staff,
  strong,
  testId,
  children,
}: {
  staff: boolean;
  strong: boolean;
  testId: string;
  children: ReactNode;
}) {
  if (staff) {
    return (
      <div role="status" data-testid={testId} style={staffBar}>
        {children}
      </div>
    );
  }
  return (
    <div className={strong ? 'wcb' : 'wcb wcb-try'} role="status" data-testid={testId}>
      <div className="wcb-in">{children}</div>
    </div>
  );
}

/* The house's pages are not under the bidder theme, so `.wcb` does not reach
   them; the same neutral bar, in the admin's own tokens. */
const staffBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  flexWrap: 'wrap',
  padding: '0.55rem 0.85rem',
  marginBottom: '1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-muted)',
  color: 'var(--text)',
  border: '1px solid var(--admin-hairline, var(--border))',
  fontSize: '0.85rem',
};

const staffButton: CSSProperties = {
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  padding: '0.2rem 0.65rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.8rem',
};
